/**
 * Region Creation Form
 * Form for creating new regions with validation
 */

import React, { useState } from 'react';
import { RegionType, RegionId } from '@maco144/shared';
import { CANVAS_THEME } from './canvas-theme';
import { useBackend } from './use-backend';

export interface RegionCreationFormProps {
  onSuccess?: (regionId: RegionId) => void;
  onCancel?: () => void;
}

type FormStep = 'input' | 'creating' | 'success' | 'error';

interface FormState {
  name: string;
  type: RegionType;
  description: string;
  isPublic: boolean;
}

const REGION_TYPE_OPTIONS: { value: RegionType; label: string; description: string }[] = [
  { value: 'channel', label: 'Channel', description: 'Real-time chat' },
  { value: 'forum', label: 'Forum', description: 'Threaded discussions' },
  { value: 'trove', label: 'Trove', description: 'Locked collaborative storage' },
  { value: 'team', label: 'Team', description: 'Team collaboration space' },
  { value: 'market', label: 'Market', description: 'Marketplace listings' },
];

/**
 * Region Creation Form
 * Features:
 * - Input fields: name, type, description, visibility
 * - Type selection with descriptions
 * - Public/private toggle
 * - Form validation
 * - Success/error feedback
 */
export const RegionCreationForm: React.FC<RegionCreationFormProps> = ({ onSuccess, onCancel }) => {
  const { client } = useBackend();
  const [step, setStep] = useState<FormStep>('input');
  const [form, setForm] = useState<FormState>({
    name: '',
    type: 'channel',
    description: '',
    isPublic: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [createdRegionId, setCreatedRegionId] = useState<RegionId | null>(null);

  const handleInputChange = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validate
    if (!form.name.trim()) {
      setError('Region name is required');
      return;
    }

    if (form.name.trim().length > 100) {
      setError('Region name must be 100 characters or less');
      return;
    }

    try {
      setStep('creating');
      setError(null);

      if (!client) {
        throw new Error('Not connected to server');
      }

      // Send create region message
      // The server will respond with a region_created event
      client.createRegion(form.type, form.isPublic, form.name.trim());

      // Wait for success - in a real implementation, this would be event-driven
      // For now, we'll simulate success after a brief delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setStep('success');
      // Generate a pseudo-ID for display (in real app, this comes from server)
      const pseudoId = `region_${Date.now()}` as RegionId;
      setCreatedRegionId(pseudoId);

      // Auto-close and callback after 2 seconds
      setTimeout(() => {
        onSuccess?.(pseudoId);
      }, 2000);
    } catch (err) {
      setStep('error');
      setError(err instanceof Error ? err.message : 'Failed to create region');
    }
  };

  if (step === 'success') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✨</div>
        <h3
          style={{
            fontSize: '18px',
            fontWeight: '600',
            color: CANVAS_THEME.textPrimary,
            margin: '0 0 8px 0',
          }}
        >
          Region Created!
        </h3>
        <p style={{ fontSize: '13px', color: CANVAS_THEME.textSecondary, margin: '0 0 24px 0' }}>
          {form.name}
        </p>
        <div
          style={{
            fontSize: '12px',
            color: CANVAS_THEME.textSecondary,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        >
          Redirecting...
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#d32f2f', margin: '0 0 8px 0' }}>
          Error
        </h3>
        <p style={{ fontSize: '13px', color: CANVAS_THEME.textSecondary, margin: '0 0 24px 0' }}>
          {error}
        </p>
        <button
          onClick={() => {
            setStep('input');
            setError(null);
          }}
          style={{
            padding: '8px 16px',
            background: 'rgba(102, 126, 234, 0.15)',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            color: CANVAS_THEME.accentColor,
            transition: `all ${CANVAS_THEME.transitionDuration}`,
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.background = 'rgba(102, 126, 234, 0.25)';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.background = 'rgba(102, 126, 234, 0.15)';
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Region Name */}
      <div>
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: CANVAS_THEME.textPrimary,
            marginBottom: '8px',
          }}
        >
          Region Name
        </label>
        <input
          type="text"
          placeholder="e.g., general, announcements, team-huddle"
          value={form.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          maxLength={100}
          disabled={step === 'creating'}
          style={{
            width: '100%',
            padding: '10px 14px',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '8px',
            fontSize: '13px',
            background: 'rgba(255, 255, 255, 0.5)',
            color: CANVAS_THEME.textPrimary,
            boxSizing: 'border-box',
            opacity: step === 'creating' ? 0.6 : 1,
            cursor: step === 'creating' ? 'not-allowed' : 'text',
            transition: `all ${CANVAS_THEME.transitionDuration}`,
          }}
        />
        <div style={{ fontSize: '11px', color: CANVAS_THEME.textSecondary, marginTop: '4px' }}>
          {form.name.length}/100
        </div>
      </div>

      {/* Region Type */}
      <div>
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: CANVAS_THEME.textPrimary,
            marginBottom: '8px',
          }}
        >
          Region Type
        </label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '8px',
          }}
        >
          {REGION_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => handleInputChange('type', option.value)}
              disabled={step === 'creating'}
              style={{
                padding: '12px 16px',
                background:
                  form.type === option.value
                    ? 'rgba(102, 126, 234, 0.2)'
                    : 'rgba(255, 255, 255, 0.3)',
                border:
                  form.type === option.value
                    ? '2px solid rgba(102, 126, 234, 0.5)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                borderRadius: '8px',
                cursor: step === 'creating' ? 'not-allowed' : 'pointer',
                textAlign: 'left',
                transition: `all ${CANVAS_THEME.transitionDuration}`,
                opacity: step === 'creating' ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (form.type !== option.value && step !== 'creating') {
                  (e.target as HTMLButtonElement).style.background = 'rgba(102, 126, 234, 0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (form.type !== option.value && step !== 'creating') {
                  (e.target as HTMLButtonElement).style.background = 'rgba(255, 255, 255, 0.3)';
                }
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: '600', color: CANVAS_THEME.textPrimary }}>
                {option.label}
              </div>
              <div
                style={{ fontSize: '11px', color: CANVAS_THEME.textSecondary, marginTop: '2px' }}
              >
                {option.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Description */}
      <div>
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: CANVAS_THEME.textPrimary,
            marginBottom: '8px',
          }}
        >
          Description (Optional)
        </label>
        <textarea
          placeholder="What is this region for?"
          value={form.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          maxLength={500}
          disabled={step === 'creating'}
          style={{
            width: '100%',
            padding: '10px 14px',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '8px',
            fontSize: '13px',
            background: 'rgba(255, 255, 255, 0.5)',
            color: CANVAS_THEME.textPrimary,
            boxSizing: 'border-box',
            minHeight: '80px',
            fontFamily: 'inherit',
            resize: 'vertical',
            opacity: step === 'creating' ? 0.6 : 1,
            cursor: step === 'creating' ? 'not-allowed' : 'text',
            transition: `all ${CANVAS_THEME.transitionDuration}`,
          }}
        />
        <div style={{ fontSize: '11px', color: CANVAS_THEME.textSecondary, marginTop: '4px' }}>
          {form.description.length}/500
        </div>
      </div>

      {/* Public/Private Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={() => handleInputChange('isPublic', !form.isPublic)}
          disabled={step === 'creating'}
          style={{
            width: '44px',
            height: '24px',
            borderRadius: '12px',
            background: form.isPublic ? 'rgba(102, 126, 234, 0.3)' : 'rgba(0, 0, 0, 0.1)',
            border: 'none',
            cursor: step === 'creating' ? 'not-allowed' : 'pointer',
            position: 'relative',
            opacity: step === 'creating' ? 0.6 : 1,
            transition: `all ${CANVAS_THEME.transitionDuration}`,
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: CANVAS_THEME.panelBackground,
              top: '2px',
              left: form.isPublic ? '22px' : '2px',
              transition: `all ${CANVAS_THEME.transitionDuration}`,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          />
        </button>
        <label style={{ fontSize: '13px', color: CANVAS_THEME.textPrimary, fontWeight: '500' }}>
          {form.isPublic ? 'Public' : 'Private'}
        </label>
      </div>

      {/* Error Message */}
      {error && step === 'input' && (
        <div
          style={{
            padding: '12px 14px',
            background: 'rgba(211, 47, 47, 0.1)',
            border: '1px solid rgba(211, 47, 47, 0.3)',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#d32f2f',
          }}
        >
          {error}
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        <button
          onClick={handleSubmit}
          disabled={step === 'creating' || !form.name.trim()}
          style={{
            flex: 1,
            padding: '12px 20px',
            background: !form.name.trim() ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.2)',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '8px',
            cursor: step === 'creating' || !form.name.trim() ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            color: CANVAS_THEME.accentColor,
            transition: `all ${CANVAS_THEME.transitionDuration}`,
            opacity: !form.name.trim() ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (form.name.trim() && step !== 'creating') {
              (e.target as HTMLButtonElement).style.background = 'rgba(102, 126, 234, 0.3)';
            }
          }}
          onMouseLeave={(e) => {
            if (form.name.trim() && step !== 'creating') {
              (e.target as HTMLButtonElement).style.background = 'rgba(102, 126, 234, 0.2)';
            }
          }}
        >
          {step === 'creating' ? '⏳ Creating...' : '✨ Create Region'}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={step === 'creating'}
            style={{
              padding: '12px 20px',
              background: 'rgba(0, 0, 0, 0.05)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              cursor: step === 'creating' ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              color: CANVAS_THEME.textSecondary,
              transition: `all ${CANVAS_THEME.transitionDuration}`,
              opacity: step === 'creating' ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (step !== 'creating') {
                (e.target as HTMLButtonElement).style.background = 'rgba(0, 0, 0, 0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (step !== 'creating') {
                (e.target as HTMLButtonElement).style.background = 'rgba(0, 0, 0, 0.05)';
              }
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};
